import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core'
import type {
  EventCellModel,
  FilterModel,
  LinkModel,
  MessageModel,
  ScheduleLayout,
  ScheduleSearchPartial,
  SectionModel,
  TableModel
} from '@flocasts/experience-service-types'
import type { AdComponentInputs } from '../shared/ads/ad/ad-component.interface'
import { AdConfig } from '../shared/ads/advertising-service/ad-config'
import {
  AD_LEADERBOARD_LARGE_SIZE,
  AD_LEADERBOARD_SMALL_SIZE,
  AD_RECTANGLE_SMALL_SIZE
} from '../shared/ads/ads.utility'
import { environment } from '../../environments/environment'
import { ActivatedRoute, ParamMap, Router } from '@angular/router'
import { ScheduleDataService } from './schedule-data.service'
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  tap
} from 'rxjs'
import { trackById } from '../shared/utility-functions/track-by-id.utility'
import { LOGGER_SERVICE } from '../logger/logger.config'
import { LoggerService } from '../logger/logger.interface'
import { isFilterModel, isLinkModel, notNullOrUndefined } from '../shared/functions/type-guards'
import { scan, shareReplay, startWith } from 'rxjs/operators'
import { isScheduleSearchPartial } from './schedule.utility'
import { SegmentService } from '../shared/analytics/services/segment.service'
import { SegmentEvents } from '../shared/analytics/models/segment-events.model'
import { ExperimentationService } from '../shared/experimentation/experimentation.service'
import { PlatformService } from '../singleton-services/platform.service'
import { ScheduleAnalyticsService } from './schedule-analytics.service'
import { PageMetaService } from '../libs/page-meta/page-meta.service'

@Component({
  selector: 'flo-schedule',
  templateUrl: './schedule.component.html',
  styleUrls: ['./schedule.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ScheduleComponent implements OnInit, OnDestroy {
  constructor(
    private readonly ar: ActivatedRoute,
    private readonly scheduleDataService: ScheduleDataService,
    private readonly router: Router,
    private readonly segmentService: SegmentService,
    private readonly experimentService: ExperimentationService,
    private readonly ps: PlatformService,
    private readonly scheduleAnalytics: ScheduleAnalyticsService,
    private readonly pageMetaService: PageMetaService,
    @Inject(LOGGER_SERVICE) private readonly logger: LoggerService
  ) {}

  ngOnInit() {
    // fire page call only once. User interactions (search query, dropdown filters, tabs) will not fire additional page calls.
    this.scheduleAnalytics.sendPageCall()
    this.scheduleAnalytics.setPageMeta()
  }

  private isFilterOrSearchLoadingSource = new BehaviorSubject<boolean>(false)
  private paginate$ = new Subject<LinkModel | undefined>()
  public isFilterOrSearchLoading$ = this.isFilterOrSearchLoadingSource.asObservable()

  private routeQueryParam$ = this.ar.queryParamMap.pipe(
    distinctUntilChanged(
      (x: ParamMap, y: ParamMap) =>
        x.get('search') === y.get('search') &&
        x.get('date') === y.get('date') &&
        x.get('facets') === y.get('facets') &&
        x.get('nav_id') === y.get('nav_id')
    ),
    shareReplay(1)
  )

  public readonly dateQueryParams$ = this.routeQueryParam$.pipe(
    map(queryParamsMap => queryParamsMap.get('date')),
    distinctUntilChanged()
  )

  public readonly searchQueryParam$ = this.routeQueryParam$.pipe(
    map(queryParamsMap => queryParamsMap.get('search')),
    distinctUntilChanged()
  )

  public trackById = trackById

  public scheduleLayout$: Observable<ScheduleLayout> = combineLatest([this.routeQueryParam$]).pipe(
    filter(() => this.ps.isBrowser),
    tap(() => this.isFilterOrSearchLoadingSource.next(true)),
    switchMap(([queryParams]) => {
      const search = queryParams.get('search') ?? undefined
      const date = queryParams.get('date') ?? undefined
      const facets = queryParams.get('facets') ?? undefined
      return this.scheduleDataService.getScheduleExperience(date, search, facets).pipe(
        filter(notNullOrUndefined),
        catchError(err => {
          this.logger.error('Error fetching data for Schedule', { err })
          return of({} as ScheduleLayout)
        })
      )
    }),
    tap(() => this.isFilterOrSearchLoadingSource.next(false)),
    shareReplay(1)
  )

  private allFilters$: Observable<SectionModel<FilterModel | LinkModel>> = this.scheduleLayout$.pipe(
    map((sl: ScheduleLayout) => sl.filters),
    filter(notNullOrUndefined),
    shareReplay(1)
  )

  public filters$: Observable<SectionModel<FilterModel>> = this.allFilters$.pipe(
    map((sm: SectionModel<FilterModel | LinkModel>) => {
      return {
        ...sm,
        items: sm.items.filter(isFilterModel)
      }
    })
  )

  public clearAll$: Observable<LinkModel | undefined> = this.allFilters$.pipe(
    map(section => section.items.filter(isLinkModel)),
    map(items => items.find(item => item.id === 'clear-all'))
  )

  // either search results or tab content data
  public tableData$ = this.scheduleLayout$.pipe(
    map(sl => {
      if (sl.searchResults && sl.searchResults?.data.length > 0) {
        return sl.searchResults.data
      } else if (sl.tabs && sl.tabs?.length > 0) {
        const tableModel = sl.tabs.find(tab => tab?.content)

        if (isScheduleSearchPartial(tableModel?.content)) {
          return tableModel?.content?.data
        }
      }

      return undefined
    })
  )

  public emitNextPage(link: LinkModel) {
    this.paginate$.next(link)
  }

  public tableDataWithPagination$ = combineLatest([
    this.tableData$,
    this.paginate$.pipe(
      startWith(undefined),
      switchMap(link => {
        if (!!link?.url) {
          // Segment track calls
          this.segmentService.trackEvent({
            name: SegmentEvents.INFINITE_SCROLL,
            properties: {
              page: link.analytics?.extraContext?.page
            }
          })

          // Paginated partial request
          return this.scheduleDataService.getSchedulePaginatedPartial(link.url)
        }
        return of(undefined)
      })
    )
  ]).pipe(
    scan<
      [(LinkModel | MessageModel | SectionModel<LinkModel | EventCellModel | TableModel>)[], ScheduleSearchPartial],
      (LinkModel | MessageModel | SectionModel<LinkModel | EventCellModel | TableModel>)[]
    >((acc, [tableData, paginatedRes]) => {
      // Only use tableData if acc is empty (i.e., first time)
      if (!acc || acc.length === 0) {
        acc = tableData ?? []
      }

      if (paginatedRes) {
        acc = acc?.filter(data => data.type === 'collection') ?? []
      }

      const paginatedData = paginatedRes?.data ?? []

      return [...acc, ...paginatedData]
    }, [])
  )

  public navigateToTab(formattedDate: string) {
    this.router.navigate([], {
      relativeTo: this.ar,
      queryParams: { date: formattedDate },
      queryParamsHandling: 'merge'
    })
  }

  public updateSearchQueryParam(searchTerm: string | null) {
    const queryParams = { search: searchTerm }
    this.router.navigate([], {
      queryParams,
      relativeTo: this.ar,
      queryParamsHandling: 'merge',
      replaceUrl: true
    })
  }

  public singleFilterClicked(facets: string): void {
    const queryParams = { facets }
    this.router.navigate([], {
      relativeTo: this.ar,
      queryParams,
      queryParamsHandling: 'merge'
    })
  }

  /* Ads Stuff */
  public AdComponent = import('../shared/ads/ad/ad.component').then(m => m.AdComponent)

  public leaderboardLarge: AdComponentInputs = {
    config: {
      adConfig: AdConfig('schedule/728x90_flex', AD_LEADERBOARD_LARGE_SIZE, 'leaderboard-large'),
      additionalOpts: {
        deferClick: environment.advertising.preventClickTimeout
      }
    }
  }

  public leaderboardSmall: AdComponentInputs = {
    config: {
      adConfig: AdConfig('schedule/728x90_flex', AD_LEADERBOARD_SMALL_SIZE, 'leaderboard-small'),
      additionalOpts: {
        deferClick: environment.advertising.preventClickTimeout
      }
    }
  }

  public leaderboardLargeBTF: AdComponentInputs = {
    config: {
      adConfig: AdConfig('schedule/728x90_flex_btf', AD_LEADERBOARD_LARGE_SIZE, 'leaderboard-large-btf'),
      additionalOpts: {
        deferClick: environment.advertising.preventClickTimeout
      }
    }
  }

  public leaderboardSmallBTF: AdComponentInputs = {
    config: {
      adConfig: AdConfig('schedule/728x90_flex_btf', AD_LEADERBOARD_SMALL_SIZE, 'leaderboard-small-btf'),
      additionalOpts: {
        deferClick: environment.advertising.preventClickTimeout
      }
    }
  }

  public rectangleSmallBTF: AdComponentInputs = {
    config: {
      adConfig: AdConfig('schedule/300x250_flex_btf', AD_RECTANGLE_SMALL_SIZE, 'rectangle-small-btf'),
      additionalOpts: {
        deferClick: environment.advertising.preventClickTimeout
      }
    }
  }

  ngOnDestroy(): void {
    this.pageMetaService.clearMeta()
  }
}
